import { map } from "rxjs/operators"
import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core"
import { NgForm } from "@angular/forms"
import { Router } from "@angular/router"
import { SearchingService } from "../../services/searching.service"
import { GTMService } from "../../services/gtm.service"
import { AddOnViewService } from "../../services/add-on-view.service"
import { UserService } from "../../services/user.service"
import { StudentGateService } from "../../services/student-gate.service"
import { BEFORE_SEARCH_PAGE_KEY } from "../../models/constants"
import Utils from "../../utils/utils"

@Component({
    selector: "app-redesign-external-search-input",
    templateUrl: "./redesign-external-search-input.component.html",
    styleUrls: ["./redesign-external-search-input.component.scss"],
    standalone: false,
})
export class RedesignExternalSearchInputComponent implements OnInit {
    @ViewChild("formObj", { static: true }) public form: NgForm
    @ViewChild("formElRef", { static: true }) public formElRef: ElementRef<HTMLElement>
    @ViewChild("submitBtn") public submitBtn: ElementRef<HTMLElement>
    @ViewChild("formInput") public formInput: ElementRef<HTMLElement>

    @Input() placeholder: string = ""
    @Input() buttonText: string = ""
    @Input() type: string = "for-browse"

    suggestions: string[] = []
    isSuggestionsOpen: boolean = false
    activeSuggestionIndex: number = -1
    showPopularHeading: boolean = true

    constructor(
        public router: Router,
        public gtmService: GTMService,
        private searchingService: SearchingService,
        private addOnService: AddOnViewService,
        private userService: UserService,
        private gradeGate: StudentGateService
    ) {}

    public ngOnInit() {
        this.form.resetForm()
    }

    public onSearch(form: NgForm) {
        const term = !!form.value?.q?.length ? form.value.q : ""
        this.performSearch(term, "external-search")
        this.closeSuggestions()
    }

    public onInputBlur(event: FocusEvent) {
        const nextFocus = event.relatedTarget as Node
        if (nextFocus && this.formElRef.nativeElement.contains(nextFocus)) return

        const term = this.form.value?.q
        if (!term) return

        this.performSearch(term, "external-search")
        this.closeSuggestions()
    }

    public onFocusOrClick(term: string) {
        if (term) {
            this.closeSuggestions()
            return
        }
        this.loadPopularSuggestions()
    }

    public onInputChange(term: string) {
        if (term) {
            this.closeSuggestions()
            return
        }
        this.loadPopularSuggestions()
    }

    public onSuggestionsKeydown(event: KeyboardEvent) {
        if (!this.isSuggestionsOpen || !this.suggestions.length) return

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                this.activeSuggestionIndex =
                    (this.activeSuggestionIndex + 1) % this.suggestions.length
                break
            case "ArrowUp":
                event.preventDefault()
                this.activeSuggestionIndex =
                    this.activeSuggestionIndex <= 0
                        ? this.suggestions.length - 1
                        : this.activeSuggestionIndex - 1
                break
            case "Enter":
                if (this.activeSuggestionIndex >= 0) {
                    event.preventDefault()
                    this.onSelectSuggestion(this.suggestions[this.activeSuggestionIndex])
                }
                break
            case "Escape":
                this.closeSuggestions()
                break
        }
    }

    public onSelectSuggestion(suggestion: string) {
        this.performSearch(suggestion, "search-autocomplete")
        this.closeSuggestions()
    }

    public closeSuggestions() {
        this.suggestions = []
        this.isSuggestionsOpen = false
        this.activeSuggestionIndex = -1
    }

    private performSearch(term: string, gtmEvent: string) {
        const queryParams: any = { q: term }
        if (this.userService.hasStudentExperience()) {
            queryParams.student = true
            if (this.gradeGate.selectedGradeValue) {
                queryParams.selected_facet = "grades:" + this.gradeGate.selectedGradeValue
            }
        }

        this.storeCurrentUrlBeforeSearch()

        this.router
            .navigate([this.addOnService.isAddonView ? "add-on/search" : "search"], {
                queryParams,
            })
            .then((_) => {
                this.form.resetForm()
                this.formInput.nativeElement.blur()
                this.submitBtn.nativeElement.blur()
            })

        this.gtmService.pushEvent(gtmEvent)
    }

    private loadPopularSuggestions() {
        this.searchingService
            .getPopularSearchTerms()
            .pipe(
                map((terms: string[]): string[] => {
                    if (!terms?.length) {
                        return []
                    }
                    const count = Math.min(5, terms.length)
                    return Utils.getRandomItems(terms, count)
                })
            )
            .subscribe((suggestions) => {
                this.suggestions = suggestions
                this.isSuggestionsOpen = suggestions.length > 0
                this.activeSuggestionIndex = -1
            })
    }

    private storeCurrentUrlBeforeSearch(): void {
        localStorage.setItem(BEFORE_SEARCH_PAGE_KEY, window.location.href)
    }
}
